// utils/company.ts

/**
 * Valid company slugs (known companies)
 * This list includes common companies and can be extended
 */
const KNOWN_VALID_COMPANIES = [
  'test',
  'superadmin',
  'wjsdigitalinnovationsltd',
  // Add more known companies here as needed
];

/**
 * Normalize company slug for development
 * 
 * In development, maps "test2000" to "test" to match the backend.
 * This prevents 404/500 errors when the frontend uses "test2000" but
 * the backend only has "test" company.
 * 
 * @param rawSlug - Raw company slug from URL or params
 * @returns Normalized company slug
 */
export function normalizeCompanySlug(rawSlug: string | undefined | null): string {
  const base = rawSlug || 'test';

  // In development, treat "test2000" as "test" to match the backend
  if (process.env.NODE_ENV === 'development' && base === 'test2000') {
    return 'test';
  }

  return base;
}

/**
 * Valid company slug patterns
 * - test, test1, test2, ..., test51 (default test companies)
 * - Any alphanumeric string (discovered from database)
 * 
 * @param slug - Company slug to validate
 * @returns true if slug is valid, false otherwise
 */
export function isValidCompanySlug(slug: string | null | undefined): boolean {
  if (!slug || typeof slug !== 'string') return false;
  
  // Reject invalid values
  if (slug === 'undefined' || slug === 'null' || slug.trim() === '') return false;
  if (slug === 'login' || slug === 'auth' || slug === 'public') return false;
  
  // Check if it's a known valid company
  if (KNOWN_VALID_COMPANIES.includes(slug)) return true;
  
  // Check if it matches test pattern (test, test1, test2, ..., test51)
  if (/^test\d{0,2}$/.test(slug)) {
    const num = slug === 'test' ? 0 : parseInt(slug.replace('test', ''), 10);
    return num >= 0 && num <= 51; // test, test1, ..., test51
  }
  
  // Allow alphanumeric slugs (discovered companies from database)
  // Must be at least 1 character, max 100 characters
  if (/^[a-zA-Z0-9_-]+$/.test(slug) && slug.length >= 1 && slug.length <= 100) {
    return true;
  }
  
  return false;
}

/**
 * Extract company slug from URL
 * Priority: URL → "test" (fallback)
 * 
 * CRITICAL: Never uses localStorage - always from URL
 * 
 * @returns Normalized company slug from URL, or "test" if invalid/not found
 */
export function getCompanySlugFromUrl(): string {
  if (typeof window === 'undefined') return 'test';
  
  const pathname = window.location.pathname;
  const parts = pathname.split('/').filter(Boolean);
  
  // Extract company from URL (first segment)
  const companyFromUrl = parts[0];
  
  // Normalize the slug first (e.g., "test2000" → "test" in development)
  const normalizedSlug = normalizeCompanySlug(companyFromUrl);
  
  // Validate the normalized slug
  if (isValidCompanySlug(normalizedSlug)) {
    return normalizedSlug;
  }
  
  // If invalid, warn and return "test"
  if (companyFromUrl && companyFromUrl !== 'login' && companyFromUrl !== 'auth') {
    console.warn('[getCompanySlugFromUrl] Invalid company slug detected:', {
      slug: companyFromUrl,
      normalized: normalizedSlug,
      pathname,
      resolvedTo: 'test',
    });
  }
  
  return 'test';
}

/**
 * Legacy function - now just calls getCompanySlugFromUrl
 * @deprecated Use getCompanySlugFromUrl instead
 */
export function resolveCompany(): string {
  return getCompanySlugFromUrl();
}
